import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { DiamondPurple } from "components/icons/DiamondPurple";
import { PriceRowTable } from "components/MyProducts/PriceRowTable";
import {
  CategoryIndent,
  coaOptions,
} from "components/Transaction/TransactionColumn";
import { LIABILITY, REVENUE } from "constants/bookkeeping";
import { itemTypesOptions } from "constants/productAndServices";
import { useChartOfAccounts } from "hooks/useChartOfAccounts";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useTypedFormikContext } from "hooks/useTypedFormikContext";
import { useGetProductCategoriesQuery } from "store/apis/productAndServices";
import { ClassificationAssignment } from "components/ClassificationAssignment/ClassificationAssignment";
import { ItemType, LinkedPriceItem } from "types/Models/productAndServices";
import { TaxRateCombobox } from "components/TaxRateCombobox/TaxRateCombobox";
import { SalesTaxCodeCombobox } from "components/SalesTaxCodeCombobox/SalesTaxCodeCombobox";
import { CategoryLabel } from "components/design/CategoryLabel";
import { flattenTypes } from "utils/flattenCOA";

type ProductFormValues = {
  name: string;
  type: ItemType;
  product_category: string;
  linked_price_items: Omit<LinkedPriceItem, "uuid">[];
  transaction_category_id: string | null;
  classification_ids: string[];
  default_tax: string | null;
  sales_tax_code: string | null;
};

export const ProductFormFields = ({
  isStripeProduct,
}: {
  isStripeProduct?: boolean;
}) => {
  const entityId = useCurrentEntityId();

  const { data: categories = [], isLoading: isCategoriesLoading } =
    useGetProductCategoriesQuery();

  const { chartOfAccounts, ...coaAPIStates } = useChartOfAccounts({});

  const revenueAndLiabilityAccounts = chartOfAccounts?.filter(
    ({ identifier_prefix }) =>
      identifier_prefix === REVENUE || identifier_prefix === LIABILITY
  );

  const { values, setFieldValue } = useTypedFormikContext<ProductFormValues>();

  const selectedTransactionCategory = flattenTypes({
    accounts: revenueAndLiabilityAccounts || [],
  }).find(({ uuid }) => uuid === values.transaction_category_id);

  const transactionCategoryValue = selectedTransactionCategory
    ? {
        label: (
          <CategoryIndent
            label={<CategoryLabel category={selectedTransactionCategory} />}
            indentLevel={0}
          />
        ),
        value: selectedTransactionCategory.uuid,
      }
    : null;

  const categoriesOptions = categories.map(({ name, uuid }) => ({
    label: name,
    value: uuid,
  }));

  const defaultCategory = categoriesOptions.find(
    ({ value }) => value === values.product_category
  );

  const defaultType = itemTypesOptions.find(
    (item) => item.value === values.type
  );

  return (
    <>
      <div className="t-grid t-grid-cols-2 t-gap-4 sm:t-grid-cols-1">
        <TextInput
          required
          name="name"
          placeholder="Enter product name"
          label="Name"
          disabled={isStripeProduct}
        />
        <Combobox
          required
          label="Type"
          options={itemTypesOptions}
          name="type"
          withForm
          placeholder="Select type"
          defaultValue={defaultType || null}
          isClearable={false}
          isDisabled={isStripeProduct}
        />
      </div>

      <div className="t-grid t-grid-cols-2 t-gap-4 sm:t-grid-cols-1">
        <Combobox
          required
          label="Group"
          isDisabled={isCategoriesLoading}
          isLoading={isCategoriesLoading}
          options={categoriesOptions}
          name="product_category"
          creatable
          withForm
          placeholder="Select or create group"
          value={defaultCategory || null}
          isClearable={false}
          menuPortalTarget={document.body}
        />
        <Combobox
          required
          label="Category"
          menuPlacement="top"
          isLoading={coaAPIStates.isLoading}
          isDisabled={coaAPIStates.isLoading}
          placeholder="Select category"
          menuPortalTarget={document.body}
          withForm
          name="transaction_category_id"
          options={coaOptions(revenueAndLiabilityAccounts || [])}
          filterOption={(v, i) =>
            v.data.data?.toLocaleLowerCase().includes(i.toLocaleLowerCase()) ||
            false
          }
          isClearable={false}
          value={transactionCategoryValue}
        />
      </div>

      <div className="t-grid t-grid-cols-2 t-gap-4 sm:t-grid-cols-1">
        <div>
          <ClassificationAssignment
            entityId={entityId}
            assignedClassificationIds={values.classification_ids || []}
            onAssignClass={(classUuid, _groupUuid, existingClassToRemove) => {
              const currentIds = values.classification_ids || [];
              const updatedClassifications = [
                ...currentIds.filter(
                  (uuid) => uuid !== existingClassToRemove && uuid !== classUuid
                ),
                classUuid,
              ];
              setFieldValue("classification_ids", updatedClassifications);
            }}
            onRemoveClass={(classUuid) => {
              const currentIds = values.classification_ids || [];
              setFieldValue(
                "classification_ids",
                currentIds.filter((uuid) => uuid !== classUuid)
              );
            }}
            label="Classification"
            maxChips={2}
            blockedUI={
              <Combobox
                label={
                  <span className="t-flex t-items-center t-gap-1">
                    Classification <DiamondPurple height={13} width={14} />
                  </span>
                }
                isMulti
                placeholder="Add group and classes"
                menuPortalTarget={document.body}
                isDisabled
                value={[]}
                options={[]}
              />
            }
          />
          <span className="t-italic t-text-subtext-sm t-font-thin t-text-text-30">
            Used for class-based income statements
          </span>
        </div>

        <div>
          <TaxRateCombobox name="default_tax" label="Default Tax (%) " />
          <span className="t-italic t-text-subtext-sm t-font-thin t-text-text-30">
            Auto-applied to invoices. You can override it anytime
          </span>
        </div>
      </div>

      <div>
        <SalesTaxCodeCombobox name="sales_tax_code" />
        <span className="t-italic t-text-subtext-sm t-font-thin t-text-text-30">
          Used to auto-calculate sales tax on invoices
        </span>
      </div>

      <PriceRowTable isDisabled={isStripeProduct} />
    </>
  );
};
